import { motion, useAnimation } from 'framer-motion';
import Link from 'next/link';
import { createElement, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import useBreakpoints from '../hooks/useBreakpoints';

export default function HeroSection() {
  const media = useBreakpoints({ defaultMedia: 'desktop' });
  const videoAnimate = useAnimation();
  const titleAnimate = useAnimation();

  const videoDesktopRef = useRef<HTMLVideoElement>(null);
  const videoMobileRef = useRef<HTMLVideoElement>(null);

  const [isSoundOn, setIsSoundOn] = useState(false);
  const isVideoLoaded = useRef(false);

  useEffect(() => {
    if (!videoDesktopRef.current || !videoMobileRef.current) return;

    const playAnimation = () => {
      if (isVideoLoaded.current) return;
      isVideoLoaded.current = true;

      videoAnimate.start({
        scale: [1, 1.04],
        opacity: [0.9, 1],
        transition: { duration: 0.6, times: [0, 1], ease: [0.65, 0, 0.35, 1] },
      });

      titleAnimate.start({
        y: [10, 10, 0],
        opacity: [0, 0, 1],
        transition: { duration: 1, times: [0, 0.6, 1], ease: [0.65, 0, 0.35, 1] },
      });
    };

    videoDesktopRef.current.addEventListener('loadeddata', playAnimation);
    videoMobileRef.current.addEventListener('loadeddata', playAnimation);
  }, []);

  return (
    <Container>
      <HeroImageWrapper>
        <VideoDesktop
          ref={videoDesktopRef}
          initial={{ scale: 1, opacity: 0.9 }}
          animate={videoAnimate}
          autoPlay
          muted={!isSoundOn || media === 'mobile'}
          loop
          playsInline
        >
          <source
            type="video/mp4"
            src="https://cleanhome-web.cleaninglab.co.kr/brandsite/about/brandstory-main_3.mp4"
          />
        </VideoDesktop>
        <VideoMobile
          ref={videoMobileRef}
          initial={{ scale: 1, opacity: 0.9 }}
          animate={videoAnimate}
          autoPlay
          muted={!isSoundOn || media !== 'mobile'}
          loop
          playsInline
        >
          <source
            type="video/mp4"
            src="https://cleanhome-web.cleaninglab.co.kr/brandsite/about/brandstory-main_mobile_3.mp4"
          />
        </VideoMobile>
        <HeroImageBlur />
      </HeroImageWrapper>
      <FrontWrapper
        onClick={() => {
          const videoMobile = videoMobileRef.current;
          const videoDesktop = videoDesktopRef.current;
          if (!videoMobile || !videoDesktop) return;

          if (media === 'mobile') {
            videoMobile.paused ? videoMobile.play() : videoMobile.pause();
          } else {
            videoDesktop.paused ? videoDesktop.play() : videoDesktop.pause();
          }
        }}
      >
        <Front>
          <TitleWrapper animate={titleAnimate} initial={{ opacity: 0 }}>
            <SubTitle>청연 이야기</SubTitle>
            <FakeHeroTitle>청소로 만난 인연, 청연</FakeHeroTitle>
            <TitleDesktop>{TitleDesktopSvg}</TitleDesktop>
            <TitleTablet>{TitleTabletSvg}</TitleTablet>
            <TitleMobile>{TitleMobileSvg}</TitleMobile>
          </TitleWrapper>
          <SoundIcon
            onClick={(e) => {
              e.stopPropagation();
              setIsSoundOn((prev) => !prev);
            }}
          >
            {isSoundOn ? SoundOnSvg : SoundOffSvg}
          </SoundIcon>
        </Front>
      </FrontWrapper>
    </Container>
  );
}

const Container = styled.section`
  position: relative;
  width: 100%;
  height: 700px;
  max-height: 100vh;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    height: 600px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    height: 580px;
  }
`;

const HeroImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const VideoDesktop = styled(motion.video)`
  position: relative;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 70% 20%;
  user-select: none;
  touch-action: none;
  pointer-events: none;
  display: block;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    display: none;
  }
`;

const VideoMobile = styled(motion.video)`
  position: relative;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 70% 20%;
  user-select: none;
  touch-action: none;
  pointer-events: none;
  display: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    display: block;
  }
`;

const SoundIcon = styled.div`
  cursor: pointer;
  width: 40px;
  height: 40px;
  border: 1px solid #ffffff;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.2);

  margin-top: 152px;
  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    margin-top: 150px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    /* max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths.mobile}; */
    margin-top: 52px;
  }
`;

const HeroImageBlur = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.15);
  pointer-events: none;
`;

const FrontWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 0 24px;
  cursor: pointer;
`;

const Front = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths.desktop};
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths.tablet};
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    /* max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths.mobile}; */
  }
`;

const TitleWrapper = styled(motion.div)`
  margin-top: 214px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-top: 320px;
  }
`;

const FakeHeroTitle = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  margin: -1px;
  clip: rect(0, 0, 0, 0);
  text-indent: -5000px;
`;

const TitleDesktop = styled.div`
  display: block;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: none;
  }
`;

const TitleTablet = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: block;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    display: none;
  }
`;

const TitleMobile = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    display: block;
  }
`;

const SubTitle = styled.h2`
  color: #ffffff;
  font-weight: 500;
  font-size: 26px;
  line-height: 140%;
  margin-bottom: 22px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    font-size: 24px;
    margin-bottom: 10px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    font-size: 18px;
    margin-bottom: 10px;
  }
`;

const TitleDesktopSvg = (
  <svg width="391" height="128" viewBox="0 0 391 128" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M23.264 6.808V1.04H7.136V6.808H23.264ZM36.032 43.32C36.032 46.624 32.952 48.696 28.864 48.696C24.664 48.696 21.752 46.624 21.752 43.32C21.752 40.072 24.664 38 28.864 38C32.952 38 36.032 40.072 36.032 43.32ZM43.256 32.12V0.983999H36.424V16.72H28.416V22.32H36.424V32.12H43.256ZM28.864 15.32V9.552H1.48V15.32H11.448C10.608 21.312 5.512 25.008 0.0240001 26.688L3.104 32.12C8.592 30.44 13.688 26.128 15.368 22.32C17.104 26.072 22.816 29.936 27.52 31.056L30.432 25.512C24.608 24.056 20.016 21.032 19.232 15.32H28.864ZM42.808 43.32C42.808 36.88 36.928 32.288 28.864 32.288C20.8 32.288 14.92 36.88 14.92 43.32C14.92 49.984 20.8 54.408 28.864 54.408C36.928 54.408 42.808 49.984 42.808 43.32ZM96.5076 42.536V36.824H76.6276V26.24H69.7396V36.824H49.8036V42.536H96.5076ZM69.1796 2.328V5.408C69.1796 14.48 61.5076 20.192 53.2196 22.432L56.2996 28.312C63.9716 25.96 70.2996 21.424 73.1556 15.32C76.1796 21.2 82.1716 25.736 90.0676 28.2L93.1476 22.376C84.9716 20.024 77.1316 14.48 77.1316 5.408V2.328H69.1796ZM140.575 30.384V24.56H114.591V19.24H139.567V2.608H107.703V8.32H132.623V13.696H107.703V30.384H120.135V36.824H100.199V42.536H146.903V36.824H127.023V30.384H140.575ZM206.946 52V46.12H182.866V33.632H175.922V52H206.946ZM184.266 22.712H173.01V9.496H184.266V22.712ZM191.154 28.48V3.728H166.178V28.48H191.154ZM213.778 19.632V13.864H205.434V1.096H198.602V37.496H205.434V19.632H213.778ZM257.342 52V46.12H233.15V33.632H226.374V52H257.342ZM264.174 19.632V13.864H255.83V1.096H248.998V36.32H255.83V19.632H264.174ZM244.742 20.416C238.526 21.704 230.35 22.376 224.078 22.152V2.944H217.246V28.312C226.262 28.648 237.91 28.144 245.302 26.464L244.742 20.416ZM282.105 16.384C282.105 23.608 287.761 29.04 295.657 29.04C303.721 29.04 309.489 23.608 309.489 16.384C309.489 9.272 303.721 3.672 295.657 3.672C287.761 3.672 282.105 9.272 282.105 16.384ZM288.881 16.328C288.881 12.352 291.793 9.496 295.657 9.496C299.633 9.496 302.713 12.352 302.713 16.328C302.713 20.304 299.633 23.16 295.657 23.16C291.793 23.16 288.881 20.304 288.881 16.328ZM325.281 51.776V45.896H300.361V33.464H293.529V51.776H325.281ZM324.161 37.272V1.096H317.329V37.272H324.161ZM331.716 16.72C331.716 24 337.372 29.544 345.436 29.544C349.916 29.544 353.668 27.808 356.132 24.952H368.004V36.712H374.78V1.096H368.004V7.76H355.516C353.052 5.296 349.524 3.784 345.436 3.784C337.372 3.784 331.716 9.496 331.716 16.72ZM375.508 52V46.12H350.98V33.632H344.148V52H375.508ZM338.548 16.664C338.548 12.632 341.46 9.72 345.436 9.72C349.3 9.72 352.268 12.632 352.268 16.664C352.268 20.696 349.3 23.72 345.436 23.72C341.46 23.72 338.548 20.696 338.548 16.664ZM358.932 19.072C359.1 18.344 359.156 17.56 359.156 16.72C359.156 15.712 359.044 14.704 358.82 13.696H368.004V19.072H358.932ZM382.448 46.68H386.144C386.144 48.976 384.464 51.44 381.608 52L382.504 55.304C387.488 54.24 390.512 49.984 390.512 45.056V39.12H382.448V46.68ZM23.264 79.808V74.04H7.136V79.808H23.264ZM36.032 116.32C36.032 119.624 32.952 121.696 28.864 121.696C24.664 121.696 21.752 119.624 21.752 116.32C21.752 113.072 24.664 111 28.864 111C32.952 111 36.032 113.072 36.032 116.32ZM43.256 105.12V73.984H36.424V89.72H28.416V95.32H36.424V105.12H43.256ZM28.864 88.32V82.552H1.48V88.32H11.448C10.608 94.312 5.512 98.008 0.0240001 99.688L3.104 105.12C8.592 103.44 13.688 99.128 15.368 95.32C17.104 99.072 22.816 102.936 27.52 104.056L30.432 98.512C24.608 97.056 20.016 94.032 19.232 88.32H28.864ZM42.808 116.32C42.808 109.88 36.928 105.288 28.864 105.288C20.8 105.288 14.92 109.88 14.92 116.32C14.92 122.984 20.8 127.408 28.864 127.408C36.928 127.408 42.808 122.984 42.808 116.32ZM50.5876 89.72C50.5876 97 56.2436 102.544 64.3076 102.544C68.7876 102.544 72.5396 100.808 75.0036 97.952H86.8756V109.712H93.6516V74.096H86.8756V80.76H74.3876C71.9236 78.296 68.3956 76.784 64.3076 76.784C56.2436 76.784 50.5876 82.496 50.5876 89.72ZM94.3796 125V119.12H69.8516V106.632H63.0196V125H94.3796ZM57.4196 89.664C57.4196 85.632 60.3316 82.72 64.3076 82.72C68.1716 82.72 71.1396 85.632 71.1396 89.664C71.1396 93.696 68.1716 96.72 64.3076 96.72C60.3316 96.72 57.4196 93.696 57.4196 89.664ZM77.8036 92.072C77.9716 91.344 78.0276 90.56 78.0276 89.72C78.0276 88.712 77.9156 87.704 77.6916 86.696H86.8756V92.072H77.8036Z"
      fill="white"
    />
  </svg>
);

const TitleTabletSvg = (
  <svg width="280" height="91" viewBox="0 0 280 91" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M16.76 4.72V0.599999H5.24V4.72H16.76ZM25.88 30.8C25.88 33.16 23.68 34.64 20.76 34.64C17.76 34.64 15.68 33.16 15.68 30.8C15.68 28.48 17.76 27 20.76 27C23.68 27 25.88 28.48 25.88 30.8ZM31.04 22.8V0.559999H26.16V11.8H20.44V15.8H26.16V22.8H31.04ZM20.76 10.8V6.68H1.2V10.8H8.32C7.72 15.08 4.08 17.72 0.16 18.92L2.36 22.8C6.28 21.6 9.92 18.52 11.12 15.8C12.36 18.48 16.44 21.24 19.8 22.04L21.88 18.08C17.72 17.04 14.44 14.88 13.88 10.8H20.76ZM30.72 30.8C30.72 26.2 26.52 22.92 20.76 22.92C15 22.92 10.8 26.2 10.8 30.8C10.8 35.56 15 38.72 20.76 38.72C26.52 38.72 30.72 35.56 30.72 30.8ZM69.0769 30.24V26.16H54.8769V18.6H49.9569V26.16H35.7169V30.24H69.0769ZM49.5569 1.52V3.72C49.5569 10.2 44.0769 14.28 38.1569 15.88L40.3569 20.08C45.8369 18.4 50.3569 15.16 52.3969 10.8C54.5569 15 58.8369 18.24 64.4769 20L66.6769 15.84C60.8369 14.16 55.2369 10.2 55.2369 3.72V1.52H49.5569ZM100.554 21.56V17.4H81.9938V13.6H99.8338V1.72H77.0738V5.8H94.8738V9.64H77.0738V21.56H85.9538V26.16H71.7138V30.24H105.074V26.16H90.8738V21.56H100.554ZM147.962 37V32.8H130.762V23.88H125.802V37H147.962ZM131.762 16.08H123.722V6.64H131.762V16.08ZM136.682 20.2V2.52H118.842V20.2H136.682ZM152.842 13.88V9.76H146.882V0.639998H142.002V26.64H146.882V13.88H152.842ZM183.958 37V32.8H166.678V23.88H161.838V37H183.958ZM188.838 13.88V9.76H182.878V0.639998H177.998V25.8H182.878V13.88H188.838ZM174.958 14.44C170.518 15.36 164.678 15.84 160.198 15.68V1.96H155.318V20.08C161.758 20.32 170.078 19.96 175.358 18.76L174.958 14.44ZM201.646 11.56C201.646 16.72 205.686 20.6 211.326 20.6C217.086 20.6 221.206 16.72 221.206 11.56C221.206 6.48 217.086 2.48 211.326 2.48C205.686 2.48 201.646 6.48 201.646 11.56ZM206.486 11.52C206.486 8.68 208.566 6.64 211.326 6.64C214.166 6.64 216.366 8.68 216.366 11.52C216.366 14.36 214.166 16.4 211.326 16.4C208.566 16.4 206.486 14.36 206.486 11.52ZM232.486 36.84V32.64H214.686V23.76H209.806V36.84H232.486ZM231.686 26.48V0.639998H226.806V26.48H231.686ZM237.083 11.8C237.083 17 241.123 20.96 246.883 20.96C250.083 20.96 252.763 19.72 254.523 17.68H263.003V26.08H267.843V0.639998H263.003V5.4H254.083C252.323 3.64 249.803 2.56 246.883 2.56C241.123 2.56 237.083 6.64 237.083 11.8ZM268.363 37V32.8H250.843V23.88H245.963V37H268.363ZM241.963 11.76C241.963 8.88 244.043 6.8 246.883 6.8C249.643 6.8 251.763 8.88 251.763 11.76C251.763 14.64 249.643 16.8 246.883 16.8C244.043 16.8 241.963 14.64 241.963 11.76ZM256.523 13.48C256.643 12.96 256.683 12.4 256.683 11.8C256.683 11.08 256.603 10.36 256.443 9.64H263.003V13.48H256.523ZM273.32 33.2H275.96C275.96 34.84 274.76 36.6 272.72 37L273.36 39.36C276.92 38.6 279.08 35.56 279.08 32.04V27.8H273.32V33.2ZM16.76 56.72V52.6H5.24V56.72H16.76ZM25.88 82.8C25.88 85.16 23.68 86.64 20.76 86.64C17.76 86.64 15.68 85.16 15.68 82.8C15.68 80.48 17.76 79 20.76 79C23.68 79 25.88 80.48 25.88 82.8ZM31.04 74.8V52.56H26.16V63.8H20.44V67.8H26.16V74.8H31.04ZM20.76 62.8V58.68H1.2V62.8H8.32C7.72 67.08 4.08 69.72 0.16 70.92L2.36 74.8C6.28 73.6 9.92 70.52 11.12 67.8C12.36 70.48 16.44 73.24 19.8 74.04L21.88 70.08C17.72 69.04 14.44 66.88 13.88 62.8H20.76ZM30.72 82.8C30.72 78.2 26.52 74.92 20.76 74.92C15 74.92 10.8 78.2 10.8 82.8C10.8 87.56 15 90.72 20.76 90.72C26.52 90.72 30.72 87.56 30.72 82.8ZM36.2769 63.8C36.2769 69 40.3169 72.96 46.0769 72.96C49.2769 72.96 51.9569 71.72 53.7169 69.68H62.1969V78.08H67.0369V52.64H62.1969V57.4H53.2769C51.5169 55.64 48.9969 54.56 46.0769 54.56C40.3169 54.56 36.2769 58.64 36.2769 63.8ZM67.5569 89V84.8H50.0369V75.88H45.1569V89H67.5569ZM41.1569 63.76C41.1569 60.88 43.2369 58.8 46.0769 58.8C48.8369 58.8 50.9569 60.88 50.9569 63.76C50.9569 66.64 48.8369 68.8 46.0769 68.8C43.2369 68.8 41.1569 66.64 41.1569 63.76ZM55.7169 65.48C55.8369 64.96 55.8769 64.4 55.8769 63.8C55.8769 63.08 55.7969 62.36 55.6369 61.64H62.1969V65.48H55.7169Z"
      fill="white"
    />
  </svg>
);

const TitleMobileSvg = (
  <svg width="238" height="77" viewBox="0 0 238 77" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14.946 3.812V0.309999H5.154V3.812H14.946ZM22.698 25.98C22.698 27.986 20.828 29.244 18.346 29.244C15.796 29.244 14.028 27.986 14.028 25.98C14.028 24.008 15.796 22.75 18.346 22.75C20.828 22.75 22.698 24.008 22.698 25.98ZM27.084 19.18V0.276H22.936V9.83H18.074V13.23H22.936V19.18H27.084ZM18.346 8.98V5.478H1.72V8.98H7.772C7.262 12.618 4.168 14.862 0.836 15.882L2.706 19.18C6.038 18.16 9.132 15.542 10.152 13.23C11.206 15.508 14.674 17.854 17.53 18.534L19.298 15.168C15.762 14.284 12.974 12.448 12.498 8.98H18.346ZM26.812 25.98C26.812 22.07 23.242 19.282 18.346 19.282C13.45 19.282 9.88 22.07 9.88 25.98C9.88 30.026 13.45 32.712 18.346 32.712C23.242 32.712 26.812 30.026 26.812 25.98ZM59.4153 25.504V22.036H47.3453V15.61H43.1633V22.036H31.0593V25.504H59.4153ZM42.8233 1.092V2.962C42.8233 8.47 38.1653 11.938 33.1333 13.298L35.0033 16.868C39.6613 15.44 43.5033 12.686 45.2373 8.98C47.0733 12.55 50.7113 15.304 55.5053 16.8L57.3753 13.264C52.4113 11.836 47.6513 8.47 47.6513 2.962V1.092H42.8233ZM86.1707 18.126V14.59H70.3947V11.36H85.5587V1.262H66.2127V4.73H81.3427V7.994H66.2127V18.126H73.7607V22.036H61.6567V25.504H90.0127V22.036H77.9427V18.126H86.1707ZM126.467 31.25V27.68H111.847V20.098H107.631V31.25H126.467ZM112.697 13.468H105.863V5.444H112.697V13.468ZM116.879 16.97V1.942H101.715V16.97H116.879ZM130.615 11.598V8.096H125.549V0.343999H121.401V22.444H125.549V11.598H130.615ZM157.065 31.25V27.68H142.377V20.098H138.263V31.25H157.065ZM161.213 11.598V8.096H156.147V0.343999H151.999V21.73H156.147V11.598H161.213ZM149.415 12.074C145.641 12.856 140.677 13.264 136.869 13.128V1.466H132.721V16.868C138.195 17.072 145.267 16.766 149.755 15.746L149.415 12.074ZM172.099 9.626C172.099 14.012 175.533 17.31 180.327 17.31C185.223 17.31 188.725 14.012 188.725 9.626C188.725 5.308 185.223 1.908 180.327 1.908C175.533 1.908 172.099 5.308 172.099 9.626ZM176.213 9.592C176.213 7.178 177.981 5.444 180.327 5.444C182.741 5.444 184.611 7.178 184.611 9.592C184.611 12.006 182.741 13.74 180.327 13.74C177.981 13.74 176.213 12.006 176.213 9.592ZM198.313 31.114V27.544H183.183V19.996H179.035V31.114H198.313ZM197.633 22.308V0.343999H193.485V22.308H197.633ZM202.221 9.83C202.221 14.25 205.655 17.616 210.551 17.616C213.271 17.616 215.549 16.562 217.045 14.828H224.253V21.968H228.367V0.343999H224.253V4.39H216.671C215.175 2.894 213.033 1.976 210.551 1.976C205.655 1.976 202.221 5.444 202.221 9.83ZM228.809 31.25V27.68H213.917V20.098H209.769V31.25H228.809ZM206.369 9.796C206.369 7.348 208.137 5.58 210.551 5.58C212.897 5.58 214.699 7.348 214.699 9.796C214.699 12.244 212.897 14.08 210.551 14.08C208.137 14.08 206.369 12.244 206.369 9.796ZM218.745 11.258C218.847 10.816 218.881 10.34 218.881 9.83C218.881 9.218 218.813 8.606 218.677 7.994H224.253V11.258H218.745ZM233.022 28.02H235.266C235.266 29.414 234.246 30.91 232.512 31.25L233.056 33.256C236.082 32.61 237.918 30.026 237.918 27.034V23.43H233.022V28.02ZM14.946 47.812V44.31H5.154V47.812H14.946ZM22.698 69.98C22.698 71.986 20.828 73.244 18.346 73.244C15.796 73.244 14.028 71.986 14.028 69.98C14.028 68.008 15.796 66.75 18.346 66.75C20.828 66.75 22.698 68.008 22.698 69.98ZM27.084 63.18V44.276H22.936V53.83H18.074V57.23H22.936V63.18H27.084ZM18.346 52.98V49.478H1.72V52.98H7.772C7.262 56.618 4.168 58.862 0.836 59.882L2.706 63.18C6.038 62.16 9.132 59.542 10.152 57.23C11.206 59.508 14.674 61.854 17.53 62.534L19.298 59.168C15.762 58.284 12.974 56.448 12.498 52.98H18.346ZM26.812 69.98C26.812 66.07 23.242 63.282 18.346 63.282C13.45 63.282 9.88 66.07 9.88 69.98C9.88 74.026 13.45 76.712 18.346 76.712C23.242 76.712 26.812 74.026 26.812 69.98ZM31.5353 53.83C31.5353 58.25 34.9693 61.616 39.8653 61.616C42.5853 61.616 44.8633 60.562 46.3593 58.828H53.5673V65.968H57.6813V44.344H53.5673V48.39H45.9853C44.4893 46.894 42.3473 45.976 39.8653 45.976C34.9693 45.976 31.5353 49.444 31.5353 53.83ZM58.1233 75.25V71.68H43.2313V64.098H39.0833V75.25H58.1233ZM35.6833 53.796C35.6833 51.348 37.4513 49.58 39.8653 49.58C42.2113 49.58 44.0133 51.348 44.0133 53.796C44.0133 56.244 42.2113 58.08 39.8653 58.08C37.4513 58.08 35.6833 56.244 35.6833 53.796ZM48.0593 55.258C48.1613 54.816 48.1953 54.34 48.1953 53.83C48.1953 53.218 48.1273 52.606 47.9913 51.994H53.5673V55.258H48.0593Z"
      fill="white"
    />
  </svg>
);

const SoundOnSvg = (
  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1 9.59599V7.53039C1 6.70197 1.67157 6.03039 2.5 6.03039H3.35889C3.49575 6.03039 3.63115 6.0023 3.7567 5.94786L8.90302 3.71644C9.56336 3.43012 10.3008 3.91417 10.3008 4.63391V12.3096C10.3008 13.0168 9.58672 13.5006 8.92992 13.2382L3.74496 11.1673C3.62697 11.1202 3.50109 11.096 3.37404 11.096H2.5C1.67157 11.096 1 10.4244 1 9.59599ZM12.7011 8.44752C12.7011 7.92813 12.2993 7.54621 11.851 7.54621V6.14621C13.115 6.14621 14.1011 7.19815 14.1011 8.44752C14.1011 9.6969 13.115 10.7488 11.851 10.7488V9.34883C12.2993 9.34883 12.7011 8.96691 12.7011 8.44752ZM11.851 5.41153C13.4408 5.41153 14.768 6.74937 14.768 8.44793C14.768 10.1465 13.4408 11.4843 11.851 11.4843V12.8843C14.2565 12.8843 16.168 10.8765 16.168 8.44793C16.168 6.01938 14.2565 4.01153 11.851 4.01153V5.41153Z"
      fill="white"
    />
  </svg>
);

const SoundOffSvg = (
  <svg width="20" height="17" viewBox="0 0 20 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.11094 6.84767V9.81943C2.11094 9.87465 2.15571 9.91943 2.21094 9.91943H3.54407C3.84898 9.91943 4.1511 9.97753 4.43426 10.0906L10.2756 12.4237V4.03825L4.48366 6.54959C4.18232 6.68025 3.85737 6.74767 3.52892 6.74767H2.21094C2.15571 6.74767 2.11094 6.79244 2.11094 6.84767ZM0.710938 6.84767V9.81943C0.710938 10.6479 1.38251 11.3194 2.21094 11.3194H3.54407C3.67111 11.3194 3.797 11.3436 3.91498 11.3908L10.3046 13.9428C10.9614 14.2052 11.6756 13.7214 11.6756 13.0142V3.42883C11.6756 2.70909 10.9381 2.22504 10.2777 2.51136L3.92673 5.26514C3.80117 5.31958 3.66577 5.34767 3.52892 5.34767H2.21094C1.38251 5.34767 0.710938 6.01924 0.710938 6.84767ZM13.8943 5.92605C14.1677 5.65268 14.6109 5.65268 14.8843 5.92605L16.4893 7.5311L18.0943 5.92611C18.3677 5.65274 18.8109 5.65274 19.0843 5.92611C19.3576 6.19948 19.3576 6.64269 19.0843 6.91606L17.4793 8.52105L19.0843 10.126C19.3576 10.3994 19.3576 10.8426 19.0843 11.116C18.8109 11.3894 18.3677 11.3894 18.0943 11.116L16.4893 9.511L14.8843 11.1161C14.6109 11.3894 14.1677 11.3894 13.8943 11.1161C13.6209 10.8427 13.6209 10.3995 13.8943 10.1261L15.4994 8.52105L13.8943 6.916C13.6209 6.64263 13.6209 6.19941 13.8943 5.92605Z"
      fill="white"
    />
  </svg>
);
